/** Alerts and webhooks in words: each alert's light, its group, its age, how it reached Johnnie. Pure. */
import type { Alert, Delivery, Light, QuietHours, Webhook } from './schemas'

export type AlertGroup = 'needs-you' | 'acknowledged' | 'cleared'

export function alertGroup(a: Pick<Alert, 'resolvedAt' | 'acknowledgedAt'>): AlertGroup {
  return a.resolvedAt ? 'cleared' : a.acknowledgedAt ? 'acknowledged' : 'needs-you'
}

export const GROUP_TITLE: Record<AlertGroup, string> = { 'needs-you': 'Needs you', acknowledged: 'Acknowledged', cleared: 'Cleared' }

/**
 * The admin's Signal rule (admin-design-r1, question 4): red means High and not yet acknowledged. Medium and
 * unacknowledged is a warning; acknowledged is ink; cleared is calm.
 */
export function alertLight(a: Pick<Alert, 'severity' | 'resolvedAt' | 'acknowledgedAt' | 'title'>): { light: Light; word: string } {
  const label = a.severity === 'high' ? 'High' : 'Medium'
  const group = alertGroup(a)
  const state = group === 'cleared' ? 'ok' : group === 'acknowledged' ? 'unknown' : a.severity === 'high' ? 'down' : 'warn'
  const word = group === 'cleared' ? 'cleared' : group === 'acknowledged' ? 'acknowledged' : 'needs you'
  return { light: { key: 'severity', label, state, detail: a.title, at: null }, word }
}

const KIND_WORDS: Record<string, string> = {
  agent_offline: 'Offline',
  run_failed: 'Run failed',
  deploy_failed: 'Deploy failed',
  box_unhealthy: 'Unhealthy',
}
export const kindWords = (kind: string) => KIND_WORDS[kind] ?? kind.replace(/_/g, ' ')
export const ALERT_KIND_FILTERS = Object.keys(KIND_WORDS)

/** Needs you first (High before Medium), then acknowledged, then cleared; newest first within each. */
export function sortAlerts(list: Alert[]): Alert[] {
  const rank = (a: Alert) => ({ 'needs-you': 0, acknowledged: 1, cleared: 2 })[alertGroup(a)] * 2 + (a.severity === 'high' ? 0 : 1)
  const at = (a: Alert) => Date.parse(a.resolvedAt ?? a.openedAt)
  return [...list].sort((x, y) => rank(x) - rank(y) || at(y) - at(x))
}

/** "7m", "2h 05m", "3d": how long it has been open, or how long it was open. */
export function openFor(a: Pick<Alert, 'openedAt' | 'resolvedAt'>, now = Date.now()): string {
  const s = Math.max(0, Math.floor(((a.resolvedAt ? Date.parse(a.resolvedAt) : now) - Date.parse(a.openedAt)) / 1000))
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ${String(m % 60).padStart(2, '0')}m`
  return `${Math.floor(h / 24)}d`
}

const who = (email: string | null) => (email ? email.split('@')[0]! : 'someone')

/** How it reached you: pushed, held for quiet hours, reminded, acknowledged, cleared by whom. */
export function reachedWords(a: Alert): string {
  if (a.resolvedAt) return a.resolvedBy === 'system' ? 'Cleared on its own' : `Resolved by ${who(a.resolvedBy)}`
  if (a.acknowledgedAt) return `Acknowledged by ${who(a.acknowledgedBy)}`
  if (a.pushHeld) return 'In the morning digest'
  const reminders = a.notifyCount - 1
  return reminders > 0 ? `Pushed · reminded ${reminders === 1 ? 'once' : `${reminders} times`}` : 'Pushed to your phone'
}

const hour = (h: number) => `${String(h).padStart(2, '0')}:00`

/** "Quiet hours 23:00 to 08:00", with "now" while they hold. Null when they're off. */
export function quietWords(q: QuietHours, quietNow: boolean): string | null {
  if (!q) return null
  return `Quiet hours ${hour(q.start)} to ${hour(q.end)}${quietNow ? ' · now' : ''}`
}

/** A webhook's scope in a few words. */
export function scopeWords(w: Pick<Webhook, 'scope' | 'founder'>): string {
  return w.scope === 'global' ? 'Every alert' : `${w.founder?.name ?? 'A founder'}'s alerts`
}

const EVENT_WORDS: Record<string, string> = {
  'alert.opened': 'Opened',
  'alert.resolved': 'Cleared',
  'alert.reminder': 'Reminder',
  'webhook.test': 'Test',
}
export const eventWords = (e: string) => EVENT_WORDS[e] ?? e

/** "Delivered (200)", "Retrying at 14:07 · answered 500", "Gave up after 6 tries · timed out after 10 s". */
export function deliveryWords(d: Pick<Delivery, 'status' | 'attempts' | 'lastStatus' | 'lastError' | 'nextAttemptAt'>, time: (iso: string) => string): string {
  if (d.status === 'delivered') return d.lastStatus ? `Delivered (${d.lastStatus})` : 'Delivered'
  const why = d.lastError ? ` · ${d.lastError}` : ''
  if (d.status === 'pending') return d.nextAttemptAt ? `Retrying at ${time(d.nextAttemptAt)}${why}` : `Sending${why}`
  return d.attempts > 1 ? `Gave up after ${d.attempts} tries${why}` : `Failed${why}`
}

/** `?open=alert:<id>`, the link every push and webhook carries. */
export function openAlertId(search: string): string | null {
  const v = new URLSearchParams(search).get('open')
  return v?.startsWith('alert:') ? v.slice('alert:'.length) || null : null
}

/** Only https, as the API checks; a plain-words reason, or null when fine. */
export function webhookUrlProblem(url: string): string | null {
  const t = url.trim()
  if (!t) return 'Paste the address that should receive alerts.'
  let u: URL
  try {
    u = new URL(t)
  } catch {
    return "That isn't a web address."
  }
  if (u.protocol !== 'https:') return 'It has to start with https://.'
  if (u.username || u.password) return 'Leave the user name and password out of the address.'
  return null
}

/** What the Alerts page's search and kind chips leave: any chosen kind, and the words in the title, detail or founder. */
export function filterAlerts(list: Alert[], f: { q: string; kinds: string[] }): Alert[] {
  const q = f.q.trim().toLowerCase()
  return list.filter(
    (a) => (!f.kinds.length || f.kinds.includes(a.kind)) && (!q || [a.title, a.detail, a.founder?.name ?? '', a.founder?.slug ?? ''].some((v) => v.toLowerCase().includes(q))),
  )
}

/** The drawer the address opens on the Alerts page: `?open=alert:<id>` (every push links there) or `?open=hook:<id>`. */
export function openOnAlerts(open: string | null): { alert: string | null; hook: string | null } {
  const alert = open?.startsWith('alert:') ? open.slice('alert:'.length) || null : null
  const hook = open?.startsWith('hook:') ? open.slice('hook:'.length) || null : null
  return { alert, hook }
}

/** One alert's record in words, for the drawer a push opens: what, how bad, when, who saw it, how it ended. */
export function alertFacts(a: Alert, time: (iso: string) => string, now = Date.now()): { label: string; value: string; mono?: boolean }[] {
  return [
    { label: 'Kind', value: kindWords(a.kind) },
    { label: 'Severity', value: a.severity === 'high' ? 'High' : 'Medium' },
    { label: 'Opened', value: `${time(a.openedAt)} · open ${openFor(a, now)}` },
    { label: 'Reached you', value: reachedWords(a) },
    { label: 'Acknowledged', value: a.acknowledgedAt ? `${time(a.acknowledgedAt)} by ${a.acknowledgedBy ?? 'someone'}` : 'not yet' },
    { label: 'Cleared', value: a.resolvedAt ? `${time(a.resolvedAt)} · ${a.resolvedBy === 'system' ? 'on its own' : `by ${a.resolvedBy ?? 'someone'}`}` : 'still open' },
    { label: 'Key', value: a.subject, mono: true },
  ]
}
